import { z } from 'zod';

import { Sire } from '@/rtk-query.gql';

/**
 * Shallow reference to a GraphQL entity ({ id, name }). Embedding the full
 * generated GQL entity types in form schemas makes react-hook-form's
 * FieldPath<T> template-literal expansion recurse through the entire
 * (mutually recursive) entity graph, which explodes type-checking time.
 */
type GqlRef<T> = Pick<T, Extract<keyof T, 'id' | 'name' | 'code'>>;

export const createSemenFormSchema = (
  isConditionalValidation: boolean,
  isSemenTypeLabelEnabled = false,
) => {
  const sharedSchema = z.object({
    dateReceived: z.date({ required_error: 'Date Received is required' }),
    sire: z.custom<GqlRef<Sire>>(
      (val) => val && typeof val === 'object',
      'Sire is required',
    ),
    owner: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
      })
      .transform((owner) => owner.id),
    colCode: z.string().min(1, 'Col Code is required'),
    storageLocation: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
      })
      .transform((storageLocation) => storageLocation.id),
    quantityReceived: z.number().int().min(1, 'Quantity Received is required'),
    cane: z.string().min(1, 'Cane is required'),
    sexType: z.enum(['Male', 'Female', 'Conventional'], {
      required_error: isSemenTypeLabelEnabled
        ? 'Semen Type is required'
        : 'Sex Type is required',
    }),
    cssCertified: z.number().int({ required_error: 'CSS Stamped is required' }),
    center: z.string().optional(),
    shortName: z.string().optional(),
    soldTo: z.string().optional(),
    secondaryOwner: z.string().optional(),
    semenType: z.string().optional(),
    units: z.number().int().optional(),
    country: z.string().optional(),
    comment: z.string().optional(),
  });

  if (isConditionalValidation) {
    return sharedSchema
      .extend({
        naab: z.string().optional(),
        export: z.number().int().optional(),
      })
      .refine(
        (data) => data.sexType === 'Conventional' || Boolean(data.naab?.length),
        {
          path: ['naab'],
          message: 'NAAB is required',
        },
      );
  }

  return sharedSchema.extend({
    naab: z.string().min(1, 'NAAB is required'),
    export: z.number().int({ required_error: 'Export is required' }),
  });
};

export const semenFormSchema = createSemenFormSchema(false);

export type SemenFormValues = z.infer<typeof semenFormSchema>;
